import css from './BroadcastProgress.module.scss';
import CsvDownloader from 'react-csv-downloader';
import { FlexRow, Text, ProgressBar, Panel, Button } from '@epam/promo';
import { ReactComponent as NavigationCloseFillIcon } from '@epam/assets/icons/navigation-close-fill.svg';
import { ReactComponent as FileImportOutlineIcon } from '@epam/assets/icons/file-import-outline.svg';

import { ReactComponent as ContentPersonGroupOutlineIcon } from '@epam/assets/icons/content-person_group-outline.svg';
import { ReactComponent as StaffingOutlineIcon } from '@epam/assets/icons/internal_logo/staffing-outline.svg';
import { ReactComponent as ActionClockFastOutlineIcon } from '@epam/assets/icons/action-clock_fast-outline.svg';
import { ReactComponent as ActionScheduleOutlineIcon } from '@epam/assets/icons/action-schedule-outline.svg';
// eslint-disable-next-line max-len
import { ReactComponent as NotificationInProgressStatusOutlineIcon } from '@epam/assets/icons/notification-in_progress_status-outline.svg';
import { ReactComponent as NotificationDoneFillIcon } from '@epam/assets/icons/notification-done-fill.svg';
import { ReactComponent as CircleLoaderIcon } from '@epam/assets/icons/loaders/circle-loader.svg';
import KeyValueRow from './KeyValueRow';
import React, { useCallback, useEffect, useState } from 'react';
import { useBroadcastStore } from '../../../store/broadcast';
import {
  BroadcastDeliveriesProgress,
  BroadcastDelivery,
  BroadcastDeliveryItemWithId,
  BroadcastDeliveryStatusesTypes,
} from '../../../models/broadcast.model';
import { dateWithTimeToString } from '../../../hooks/helpers/date-time-helper';
import {
  getBroadcastDeliveriesByAllStatuses,
  getBroadcastDeliveriesProgress,
} from '../../../api/broadcasts.service';
import { Datas } from 'react-csv-downloader/src/lib/csv';
import columns from './BroadcastLogs/tableColumns';
import { useModal } from '../../../hooks';
import BroadcastLogs from './BroadcastLogs/BroadcastLogs';
import TimestampRow from './TimestampRow';
import { BroadcastStatuses } from 'interfaces';

const mapReport = (
  data: Record<BroadcastDeliveryStatusesTypes, BroadcastDelivery>,
): BroadcastDeliveryItemWithId[] => {
  return Object.values(data).flatMap((data) =>
    data.items.map((i) => ({ ...i, id: crypto.randomUUID() })),
  );
};

export const BroadcastProgress = () => {
  const [data, setData] = useState<BroadcastDeliveriesProgress | null>(null);
  const [reportData, setReportData] = useState<BroadcastDeliveryItemWithId[]>(
    [],
  );
  const [isReportLoading, setIsReportLoading] = useState(false);
  const modal = useModal();
  const {
    draft: { id, status, title },
  } = useBroadcastStore((state) => state);

  const fetchReportData = useCallback(async (id: string) => {
    setIsReportLoading(true);
    try {
      const result = await getBroadcastDeliveriesByAllStatuses(id);

      return mapReport(result);
    } catch (err) {
      console.error(err);

      return [];
    } finally {
      setIsReportLoading(false);
    }
  }, []);

  const openLogsModal = useCallback(async () => {
    modal.showdata({
      data: {
        title: 'Broadcast logs',
      },
      width: 1000,
      children: (
        <BroadcastLogs
          isReportLoading={isReportLoading}
          reportData={reportData}
        />
      ),
    });
  }, [isReportLoading, modal, reportData]);

  useEffect(() => {
    if (id) {
      fetchReportData(id).then((res) => setReportData(res));
      getBroadcastDeliveriesProgress(id).then((res) => setData(res));
    }
  }, [fetchReportData, id]);

  if (!data || status !== BroadcastStatuses.PUBLISHED) {
    return null;
  }

  return (
    <Panel shadow cx={css.page__container}>
      <FlexRow cx={css.progress__header}>
        <Text cx={css.progress__title} size="24">
          Broadcast Progress
        </Text>
      </FlexRow>

      <Panel cx={css.progress__body}>
        <TimestampRow
          Icon={ActionClockFastOutlineIcon}
          title="Started at"
          value={data.startedAt ? dateWithTimeToString(data.startedAt) : '-'}
        />
        <TimestampRow
          Icon={ActionScheduleOutlineIcon}
          title="Completed at"
          value={
            data.completedAt ? dateWithTimeToString(data.completedAt) : '-'
          }
        />

        <FlexRow cx={css['progress__row-container']}>
          <KeyValueRow
            Icon={NotificationDoneFillIcon}
            iconColor="var(--uui-success-50)"
            title="Delivered"
            value={data.deliveredCount}
          />
          <KeyValueRow
            Icon={NavigationCloseFillIcon}
            iconColor="var(--uui-error-50)"
            title="Failed"
            value={data.failedCount}
          />
        </FlexRow>

        <FlexRow cx={css['progress__row-container']}>
          <KeyValueRow
            Icon={NotificationInProgressStatusOutlineIcon}
            title="In progress"
            value={data.pendingCount}
          />
          <KeyValueRow
            Icon={ContentPersonGroupOutlineIcon}
            title="Total"
            value={data.totalRecipients}
          />
        </FlexRow>

        <ProgressBar
          cx={css['progress-bar']}
          label={`${data.progressPercentage}% complete`}
          progress={data.progressPercentage}
          size="18"
        />

        <Panel cx={css.progress__controls}>
          <CsvDownloader
            className={css.csv_button__wrapper}
            columns={columns.map(({ key, caption }) => ({
              id: key,
              displayName: caption,
            }))}
            datas={reportData as unknown as Datas}
            extension=".csv"
            filename={`${title.replaceAll(/\s+/g, '_')}`}
            prefix="broadcast_report"
            suffix={false}
            title={`Progress report for broadcast: ${title}, id: ${id}`}>
            <Button
              caption="Download report"
              color="primary"
              cx={css.csv_button}
              fill="light"
              icon={isReportLoading ? CircleLoaderIcon : FileImportOutlineIcon}
              isDisabled={isReportLoading}
              size="36"
            />
          </CsvDownloader>

          <Button
            caption="View full logs"
            color="primary"
            fill="light"
            icon={isReportLoading ? CircleLoaderIcon : StaffingOutlineIcon}
            isDisabled={isReportLoading}
            size="36"
            onClick={openLogsModal}
          />
        </Panel>
      </Panel>
    </Panel>
  );
};
